import { listGuidanceContents } from '../graphql/queries';
import { updateGuidanceContent, deleteGuidanceContent } from '../graphql/mutations';
import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  makeStyles,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Snackbar,
  Paper,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useParams, useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '15px',
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  tableCell: {
    padding: theme.spacing(1, 2),
  },
  actionButton: {
    margin: theme.spacing(0, 1),
  },
  deleteButton: {
    backgroundColor: '#f73636', // Your desired color
    '&:hover': {
      backgroundColor: '#f74f4f', // Your desired hover color
    },
  },
  customButton: {
    backgroundColor: '#01b5e2', // Your desired color
    '&:hover': {
      backgroundColor: '#01b5ff', // Your desired hover color
    },
  },
}));

const ManageGuidanceArticle = ({ guidanceId }) => {
  const classes = useStyles();
  const [guidanceContents, setGuidanceContents] = useState([]);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const params = useParams();

  const handleEditClick = (pageId) => {
    navigate(`/AddGuidanceContentForm/${params.guidanceId}?pageId=${pageId}`);
  };

  
  useEffect(() => {
    fetchGuidanceContents();
  }, []);

  const fetchGuidanceContents = async () => {
    let allGuidanceContents = [];
    let nextToken = null;

    try {
      do {
        const response = await API.graphql(
          graphqlOperation(listGuidanceContents, {
            filter: {
              guidanceID: {
                eq: params.guidanceId,
              },
            },
            nextToken: nextToken,
          })
        );
        
        const guidanceContentList = response.data?.listGuidanceContents?.items || [];
        allGuidanceContents = [...allGuidanceContents, ...guidanceContentList];
        nextToken = response.data?.listGuidanceContents?.nextToken;
      } while (nextToken);

      // Sorting the contents by 'order'
      const sortedGuidanceContents = allGuidanceContents.sort(
        (a, b) => a.order - b.order
      );
      setGuidanceContents(sortedGuidanceContents);
    } catch (error) {
      console.log(error);
    }
  };


  const handleDeleteClick = async (id) => {
    try {
      await API.graphql(graphqlOperation(deleteGuidanceContent, {
        input: { id }
      }));
      fetchGuidanceContents();  // Refresh the list
    } catch (error) {
      console.log(error);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const updatedContents = Array.from(guidanceContents);
    const [removed] = updatedContents.splice(result.source.index, 1);
    updatedContents.splice(result.destination.index, 0, removed);

    setGuidanceContents(updatedContents);
  };

  const handleSaveOrder = async () => {
    try {
      const updatedGuidanceContents = guidanceContents.map((content, index) => ({
        ...content,
        order: index + 1, // Assuming the order starts from 1, adjust accordingly if needed
      }));

      const promises = updatedGuidanceContents.map((content) =>
        API.graphql(
          graphqlOperation(updateGuidanceContent, {
            input: {
              id: content.id,
              order: content.order,
            },
          })
        )
      );

      await Promise.all(promises);
      fetchGuidanceContents();
      setIsSnackbarOpen(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false); // Close the Snackbar
  };

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Guidance Pages
      </Typography>
      <Typography variant="h6" component="h3" gutterBottom>
        Drag rows to re-order
      </Typography>
      <DragDropContext onDragEnd={handleDragEnd}>
      <Paper className={classes.tableContainer}>
        <Table>
          <TableHead>
          <TableRow className={classes.tableHeader}>
              <TableCell>ID</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          {guidanceContents.length > 0 ? (
            <Droppable droppableId="guidanceContents">
              {(provided) => (
                <TableBody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {guidanceContents.map((content, index) => (
                    <Draggable
                      key={content.id}
                      draggableId={content.id}
                      index={index}
                    >
                      {(provided) => (
                        <TableRow
                          className={classes.guidanceContentItem}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          
                          <TableCell>{content.id}</TableCell>
                          <TableCell>{content.order}</TableCell>
                          <TableCell>{content.title}</TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.customButton}
                              onClick={() => handleEditClick(content.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              className={classes.deleteButton}
                              onClick={() => handleDeleteClick(content.id)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={2}>No guidance content available</TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        </Paper>
      </DragDropContext>
      <Button
        variant="contained"
        color="primary"
        className={classes.customButton}
        onClick={handleSaveOrder}
        disabled={guidanceContents.length === 0}
      >
        Save Order
      </Button>

      <Snackbar open={isSnackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert severity="success">
          Save successful
        </Alert>
      </Snackbar>
    </>

  );
};

export default ManageGuidanceArticle;
